/**
 * Iter-334 Phase A — Public Marketplace directory page.
 * URL: /marketplace
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Marketplace() {
  const [search, setSearch] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = search.get('category') || '';
  const q = search.get('q') || '';

  useEffect(() => {
    axios.get(`${API}/marketplace/categories`).then(r => setCats(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (q) params.set('q', q);
    axios.get(`${API}/marketplace/vendors?${params}`)
      .then(r => setVendors(r.data))
      .finally(() => setLoading(false));
  }, [category, q]);

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-xs uppercase tracking-widest text-teal-300">iEvntür Marketplace</div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-2" data-testid="marketplace-title">Find the perfect vendor for your event</h1>
          <p className="text-slate-300 mt-3 max-w-xl">Photographers, DJs, caterers, planners, and more — book instantly with secure payments. No account required.</p>
          <div className="mt-6 max-w-lg">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search vendors, cities, styles…"
                className="pl-9 bg-white text-slate-900"
                defaultValue={q}
                onKeyDown={(e) => { if (e.key === 'Enter') setSearch({ ...(category && { category }), ...(e.currentTarget.value && { q: e.currentTarget.value }) }); }}
                data-testid="marketplace-search"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap gap-2 overflow-x-auto">
          <button onClick={() => setSearch({})} className={`whitespace-nowrap px-3 py-1 rounded-full text-sm ${!category ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid="cat-all">All</button>
          {cats.map((c) => (
            <button
              key={c.slug}
              onClick={() => setSearch({ category: c.slug })}
              className={`whitespace-nowrap px-3 py-1 rounded-full text-sm ${category === c.slug ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              data-testid={`cat-${c.slug}`}
            >{c.name}</button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="py-16 flex justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading vendors…</div>
        ) : vendors.length === 0 ? (
          <div className="py-16 text-center text-slate-500" data-testid="marketplace-empty">
            No vendors match your filters yet. Check back soon!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map((v) => (
              <Link key={v.slug} to={`/vendor/${v.slug}`} className="group bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`vendor-card-${v.slug}`}>
                <div className="aspect-[16/9] bg-slate-100 bg-cover bg-center relative" style={{ backgroundImage: v.cover_url ? `url(${v.cover_url})` : 'none' }}>
                  {!v.cover_url && <div className="h-full flex items-center justify-center text-slate-400 text-sm">No cover yet</div>}
                  {v.starting_price != null && (
                    <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur text-slate-900 text-xs font-semibold px-2 py-1 rounded-full">
                      from ${Number(v.starting_price).toFixed(0)}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    {v.logo_url && <img src={v.logo_url} alt="" className="w-6 h-6 rounded" />}
                    <span className="font-semibold text-slate-900 truncate group-hover:text-teal-700">{v.company_name}</span>
                    {v.avg_rating != null && (
                      <span className="ml-auto text-xs flex items-center gap-0.5 text-amber-600">
                        <span>★</span><span className="font-medium">{v.avg_rating}</span>
                      </span>
                    )}
                  </div>
                  {v.category && <div className="text-xs text-teal-700 mb-1">{v.category.name}</div>}
                  {v.tagline && <div className="text-sm text-slate-600 line-clamp-2">{v.tagline}</div>}
                  {(v.city || v.state) && <div className="text-xs text-slate-500 mt-2">{[v.city, v.state].filter(Boolean).join(', ')}</div>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
